import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { supabase } from './supabase';
import { playAudioFromPath } from './audio';
import { isGroupMuted, isInDND } from './groupMute';

// Web port of app/src/lib/messages.ts. Same correctness fixes:
//   - Backfill on SUBSCRIBED even with null cursor (pull last 50, dedupe)
//   - .gte instead of .gt so ties aren't dropped
//   - id-set dedupe in seenIdsRef is authoritative
// No haptics (web has limited vibrate support, and it'd misfire on desktop).
// Mute: per-group mute + DND suppress playback; urgent always bypasses.

export type MessageRow = {
  id: string;
  group_id: string;
  sender_id: string;
  recipient_id: string | null;
  audio_path: string | null;
  transcript: string | null;
  duration_ms: number | null;
  urgency: 'urgent' | null;
  created_at: string;
};

export function useIncomingMessages(selfId: string, groupId: string | null) {
  const [messages, setMessages] = useState<MessageRow[]>([]);
  const [lastSeenAt, setLastSeenAt] = useState<string>(() => new Date().toISOString());
  const selfRef = useRef(selfId);
  selfRef.current = selfId;
  const newestCreatedAtRef = useRef<string | null>(null);
  const seenIdsRef = useRef<Set<string>>(new Set());

  const mergeRows = useCallback((rows: MessageRow[]) => {
    if (rows.length === 0) return;
    setMessages((prev) => {
      const byId = new Map(prev.map((m) => [m.id, m]));
      for (const r of rows) byId.set(r.id, r);
      return Array.from(byId.values())
        .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
        .slice(0, 50);
    });
    for (const r of rows) {
      seenIdsRef.current.add(r.id);
      if (
        !newestCreatedAtRef.current ||
        new Date(r.created_at).getTime() > new Date(newestCreatedAtRef.current).getTime()
      ) {
        newestCreatedAtRef.current = r.created_at;
      }
    }
  }, []);

  const handleLive = useCallback((row: MessageRow, isBackfill: boolean) => {
    if (seenIdsRef.current.has(row.id)) return;
    seenIdsRef.current.add(row.id);
    setMessages((prev) => {
      if (prev.some((m) => m.id === row.id)) return prev;
      return [row, ...prev].slice(0, 50);
    });
    if (
      !newestCreatedAtRef.current ||
      new Date(row.created_at).getTime() > new Date(newestCreatedAtRef.current).getTime()
    ) {
      newestCreatedAtRef.current = row.created_at;
    }
    if (row.sender_id === selfRef.current) return;
    if (row.recipient_id !== null && row.recipient_id !== selfRef.current) return;
    // No auto-play on backfill (a 20-minute-away return shouldn't unleash a flood).
    if (isBackfill) return;
    if (!row.audio_path) return;
    const urgent = row.urgency === 'urgent';
    if (!urgent && (isGroupMuted(row.group_id) || isInDND(row.group_id))) return;
    playAudioFromPath(row.audio_path, urgent ? 1 : 0.85).catch((e) =>
      console.warn('playback failed', e)
    );
  }, []);

  const backfill = useCallback(
    async (gid: string) => {
      const cursor = newestCreatedAtRef.current;
      let query = supabase
        .from('messages')
        .select('*')
        .eq('group_id', gid)
        .order('created_at', { ascending: true })
        .limit(50);
      if (cursor) query = query.gte('created_at', cursor);
      const { data, error } = await query;
      if (error) {
        console.warn('message backfill failed', error.message);
        return;
      }
      for (const r of (data ?? []) as MessageRow[]) {
        handleLive(r, true);
      }
    },
    [handleLive]
  );

  useEffect(() => {
    newestCreatedAtRef.current = null;
    seenIdsRef.current = new Set();
    if (!groupId) {
      setMessages([]);
      return;
    }
    let cancelled = false;
    (async () => {
      const { data, error } = await supabase
        .from('messages')
        .select('*')
        .eq('group_id', groupId)
        .order('created_at', { ascending: false })
        .limit(50);
      if (cancelled) return;
      if (!error && data) mergeRows(data as MessageRow[]);
    })();
    return () => {
      cancelled = true;
    };
  }, [groupId, mergeRows]);

  useEffect(() => {
    if (!groupId) return;
    const channel = supabase
      .channel(`messages-feed-${groupId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'messages',
          filter: `group_id=eq.${groupId}`,
        },
        (payload) => {
          handleLive(payload.new as MessageRow, false);
        }
      )
      .subscribe((status) => {
        if (status === 'SUBSCRIBED') backfill(groupId).catch(() => {});
      });
    return () => {
      supabase.removeChannel(channel);
    };
  }, [groupId, handleLive, backfill]);

  useEffect(() => {
    if (!groupId) return;
    const onVis = () => {
      if (document.visibilityState === 'visible') backfill(groupId).catch(() => {});
    };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, [groupId, backfill]);

  const unreadCount = useMemo(() => {
    const seen = new Date(lastSeenAt).getTime();
    return messages.filter(
      (m) =>
        m.sender_id !== selfId &&
        (m.recipient_id === null || m.recipient_id === selfId) &&
        new Date(m.created_at).getTime() > seen
    ).length;
  }, [messages, lastSeenAt, selfId]);

  function markAllRead() {
    setLastSeenAt(new Date().toISOString());
  }

  return { messages, unreadCount, markAllRead };
}
